/**
 * UserMenu.tsx — avatar do usuário com dropdown de info + sign out.
 *
 * - Avatar tenta Gravatar (sha256 do email, padrão atual desde 2024).
 *   Se Gravatar 404, cai pra iniciais.
 * - Click abre popover com nome, email, grupos e botão Sair.
 * - ESC ou click-outside fecham.
 * - Sem deps extras (sem Radix).
 */

import { useEffect, useId, useRef, useState } from 'react';
import { SignOut } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'motion/react';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils/cn';
import type { AuthUser } from '@/lib/auth/AuthContext';

interface UserMenuProps {
  user: AuthUser;
  signingOut: boolean;
  onSignOut: () => void;
}

/** Iniciais a partir do nome ou email (até 2 chars). */
function initialsFor(user: AuthUser): string {
  const source = user.name?.trim() || user.email.split('@')[0] || '?';
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

/** sha256 hex via SubtleCrypto (browser stdlib). */
async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const hashBuf = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Hook: deriva URL Gravatar a partir do email. Retorna null até ficar pronto. */
function useGravatarUrl(email: string, size = 96): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const normalized = email.trim().toLowerCase();
    if (!normalized) {
      setUrl(null);
      return;
    }
    void sha256Hex(normalized).then((hash) => {
      if (cancelled) return;
      // d=404 → Gravatar retorna 404 quando não há foto, aí caímos pra iniciais
      setUrl(`https://www.gravatar.com/avatar/${hash}?d=404&s=${size}`);
    });
    return () => {
      cancelled = true;
    };
  }, [email, size]);
  return url;
}

interface AvatarImgProps {
  src: string | null;
  alt: string;
  initials: string;
  /** px */
  size: number;
}

function AvatarImg({ src, alt, initials, size }: AvatarImgProps) {
  const [failed, setFailed] = useState(false);
  const showImg = src && !failed;

  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full',
        'border border-border bg-surface-muted',
      )}
      style={{ width: size, height: size }}
    >
      {showImg ? (
        <img
          src={src}
          alt={alt}
          width={size}
          height={size}
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <span
          className={cn(
            'font-mono font-semibold tracking-tight text-muted',
            size <= 36 ? 'text-[0.6875rem]' : 'text-sm',
          )}
        >
          {initials}
        </span>
      )}
    </span>
  );
}

export function UserMenu({ user, signingOut, onSignOut }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelId = useId();

  const initials = initialsFor(user);
  const gravatar = useGravatarUrl(user.email, 96);
  // Prioridade: foto do IdP (Google `picture` claim, se mapeado) > Gravatar > iniciais.
  const avatarSrc = user.picture ?? gravatar;
  const altLabel = user.name ?? user.email ?? 'Usuário';

  // click-outside + ESC
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const displayName = user.name?.trim() || user.email.split('@')[0] || 'Usuário';
  const groups = user.groups ?? [];

  const primaryGroup = groups[0]?.replace(/^Aura-/, '') ?? 'Sessão ativa';

  return (
    <div ref={wrapperRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`Conta de ${altLabel}`}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-lg border border-transparent px-2 py-2 text-left',
          'transition-[background-color,border-color,box-shadow] hover:border-border hover:bg-surface-muted/55',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
          open && 'border-border bg-surface-muted/70 shadow-soft',
        )}
      >
        <AvatarImg src={avatarSrc} alt={altLabel} initials={initials} size={34} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[0.8125rem] font-semibold leading-tight text-ink">
            {displayName}
          </span>
          <span className="mt-0.5 block truncate font-mono text-[0.65rem] text-muted">
            {user.email || primaryGroup}
          </span>
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            id={panelId}
            role="menu"
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ duration: 0.12, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'absolute bottom-[calc(100%+8px)] left-0 right-0 z-30 origin-bottom-left',
              'max-h-[min(28rem,calc(100dvh-2rem))] overflow-y-auto rounded-xl border border-border bg-surface shadow-lg shadow-ink/[0.08]',
            )}
          >
            {/* Header com avatar grande */}
            <div className="flex items-center gap-3 border-b border-border p-3">
              <AvatarImg src={avatarSrc} alt={altLabel} initials={initials} size={44} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[0.8125rem] font-semibold leading-tight text-ink">
                  {displayName}
                </p>
                <p className="mt-0.5 truncate font-mono text-[0.6875rem] text-muted">
                  {user.email}
                </p>
              </div>
            </div>

            {/* Grupos */}
            {groups.length > 0 ? (
              <div className="flex flex-col gap-2 border-b border-border px-3 py-3">
                <p className="font-mono text-[0.625rem] uppercase tracking-[0.16em] text-muted">
                  Grupos
                </p>
                <div className="flex flex-wrap gap-1">
                  {groups.map((g) => (
                    <span
                      key={g}
                      className={cn(
                        'rounded-md border border-border bg-surface-muted',
                        'px-1.5 py-0.5 font-mono text-[0.6875rem] text-ink',
                      )}
                    >
                      {g}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="border-b border-border px-3 py-3">
                <p className="text-[0.75rem] text-muted">Sem grupos atribuídos.</p>
              </div>
            )}

            {/* Footer ações */}
            <div className="flex items-center justify-between gap-2 px-3 py-2.5">
              <span className="font-mono text-[0.625rem] uppercase tracking-[0.14em] text-muted">
                Sessão
              </span>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  setOpen(false);
                  onSignOut();
                }}
                loading={signingOut}
                aria-label="Sair"
              >
                <SignOut size={13} weight="bold" aria-hidden="true" />
                Sair
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
